import { useEffect, useRef, useState } from 'react';
import type { ListFilter } from '@/api/generated/schemas';
import type { EntitySelectorHandle, NoPickFilters } from '@/shared/ui/async-selector';

/** AsyncSelect nad catalog kritériem uloženým ve formuláři jako `number[]`. */
export function CatalogIdsSelector<T>({
  selector,
  ids,
  onChange,
  getId,
  extraFilters,
  label,
  placeholder,
}: {
  selector: EntitySelectorHandle<T, NoPickFilters>;
  ids: number[];
  onChange: (ids: number[]) => void;
  getId: (item: T) => number;
  extraFilters?: ListFilter[];
  label: string;
  placeholder?: string;
}) {
  const [items, setItems] = useState<T[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const idsKey = ids.join(',');
  const extraKey = JSON.stringify(extraFilters ?? []);

  useEffect(() => {
    if (ids.length === 0) {
      setItems([]);
      return;
    }

    const currentIds = itemsRef.current.map(getId);
    if (currentIds.length === ids.length && currentIds.every((id, index) => id === ids[index])) {
      return;
    }

    let cancelled = false;
    void selector
      .createOptions()
      .resolveByIds?.(ids, {} as NoPickFilters, extraFilters)
      .then((resolved) => {
        if (!cancelled && resolved) {
          setItems([...resolved]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ids, idsKey, extraKey, extraFilters, getId, selector]);

  const Selector = selector.Selector;
  return (
    <Selector
      mode="multi"
      label={label}
      selected={items}
      extraFilters={extraFilters}
      placeholder={placeholder}
      onChange={(next) => {
        const nextItems = [...next];
        setItems(nextItems);
        onChange(nextItems.map(getId));
      }}
    />
  );
}
